/**
 * Admin role + permission matrix for chiroup-admin.
 *
 * Single source of truth shared by the admin frontend and the admin backend.
 * The matrix below mirrors `docs/admin-role-matrix.csv` 1:1: each entry in
 * PERMISSION_MATRIX is one row of the matrix, listing the non-super-admin
 * roles that hold that permission. super_admin always gets every permission.
 *
 * To add or change a capability:
 *   1. Add (or edit) a row in PERMISSION_MATRIX with the right role list.
 *   2. Use the new permission string in the relevant `endpointWrapper`
 *      `permissions: [...]` and/or `hasPermission(...)` UI checks.
 *
 * Do not maintain a separate role→permissions map; everything is derived.
 */

export const ADMIN_USER_ROLE_KEYS = [
  'super_admin',
  'admin',
  'developer',
  'tech_admin',
  'account_admin',
  'account_support',
  'standard_user',
  'marketing',
  'finance',
  'researcher',
] as const;

export type AdminUserRoleKey = (typeof ADMIN_USER_ROLE_KEYS)[number];

export const ROLE_LABELS: Record<AdminUserRoleKey, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  developer: 'Developer',
  tech_admin: 'Tech Admin',
  account_admin: 'Account Admin',
  account_support: 'Account Support',
  standard_user: 'Standard User',
  marketing: 'Marketing/Content',
  finance: 'Finance',
  researcher: 'Researcher',
};

/** Roles other than super_admin, which auto-grants everything. */
type NonSuperRole = Exclude<AdminUserRoleKey, 'super_admin'>;

/**
 * The matrix. Each key is a permission string; each value is the set of
 * non-super-admin roles that hold it. super_admin is intentionally omitted
 * — it always holds every permission via expandPermissions.
 *
 * Permissions are grouped by page to mirror the CSV reading order. Keep
 * them grouped so a diff against the matrix is obvious.
 */
const PERMISSION_MATRIX = {
  // ── Analytics ─────────────────────────────────────────────────────────
  'analytics.view': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
    'finance',
  ],
  'analytics.stripe.view': ['admin'],
  'analytics.ehr_npi.view': ['admin'],

  // ── Clinics Map ───────────────────────────────────────────────────────
  'clinic_map.view': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
    'finance',
  ],

  // ── Users (top-level page) ────────────────────────────────────────────
  'users.view': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
  ],
  'users.create': ['admin', 'account_admin'],
  'users.edit': ['admin', 'account_admin'],
  'users.remove_from_clinics': ['admin', 'tech_admin', 'account_admin', 'account_support'],
  'users.reinstate_education_membership': ['admin', 'account_admin'],
  'users.reset_password': [
    'admin',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
  ],
  'users.provision_sandbox': ['admin', 'developer', 'tech_admin'],

  // ── Clinics ───────────────────────────────────────────────────────────
  'clinic.view': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
  ],
  'clinic.test_patient': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
  ],
  'clinic.create': ['admin', 'developer', 'account_admin'],
  'clinic.view_general': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
  ],
  'clinic.edit_general': ['admin', 'developer', 'tech_admin', 'account_admin'],
  'clinic.view_event_logs': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
  ],
  'clinic.view_users': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
    'finance',
  ],
  'clinic.change_user_roles': ['admin', 'developer', 'tech_admin', 'account_admin'],
  'clinic.remove_user': ['admin', 'developer', 'account_admin'],
  'clinic.add_users': ['admin', 'account_admin'],
  'clinic.view_location': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
  ],
  'clinic.edit_location': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
  ],
  'clinic.add_location': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
  ],
  'clinic.view_billing': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
  ],
  'clinic.edit_billing': ['admin', 'developer', 'account_admin'],
  'clinic.view_integrations': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
  ],
  'clinic.toggle_integrations': ['admin', 'tech_admin'],
  'clinic.view_pp_config': [
    'admin',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
  ],
  'clinic.edit_pp_config': ['admin', 'tech_admin', 'account_admin'],
  'clinic.view_disabled_patients': ['admin', 'tech_admin'],
  'clinic.debug': ['admin', 'developer', 'tech_admin'],
  'clinic.exports': ['admin', 'tech_admin'],
  'clinic.adhoc_reports': ['admin', 'developer', 'tech_admin'],
  // Time-bound "open the clinic in support mode" capability. Matrix grants
  // it to everyone except the content-only roles (marketing, finance,
  // researcher).
  'clinic.support_access': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
  ],
  // Not in the matrix. Used by the teardown messaging endpoint; kept
  // restricted to super_admin + admin so existing prod usage works.
  'clinic.teardown_messaging': ['admin'],

  // ── Bulk campaign approvals ──────────────────────────────────────────
  // Reviewing a clinic's request to send a second bulk text campaign inside
  // the rolling 24-hour window. Approving lifts only that rule — carrier
  // throughput and daily part ceilings are enforced regardless — so the same
  // roles that already handle clinic account decisions hold it.
  'campaign.approve_bulk_sms': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
  ],

  // ── Secure Case Workspaces ───────────────────────────────────────────
  // Purpose-bound PHI workspaces linked to a clinic and Salesforce case.
  // Content access is determined by these permissions; workspaces do not
  // require separate user grants.
  'secure_cases.view': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
  ],
  'secure_cases.create': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
  ],
  'secure_cases.view_phi': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
  ],
  'secure_cases.upload': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
  ],
  'secure_cases.download': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
  ],
  'secure_cases.manage': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
  ],
  'secure_cases.legal_hold': ['admin'],

  // ── Research ──────────────────────────────────────────────────────────
  'research.view': [
    'admin',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
    'researcher',
  ],
  'research.create': ['admin', 'marketing', 'researcher'],
  'research.edit': ['admin', 'marketing', 'researcher'],
  'research.export': ['admin', 'marketing', 'researcher'],
  'research.analytics': [
    'admin',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
    'researcher',
  ],

  // ── Disabilities ──────────────────────────────────────────────────────
  'disabilities.view': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
  ],
  'disabilities.edit': ['admin', 'developer'],

  // ── Tests ─────────────────────────────────────────────────────────────
  'tests.view': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
    'finance',
    'researcher',
  ],
  'tests.edit': ['admin', 'developer'],

  // ── Home Page ─────────────────────────────────────────────────────────
  'home_page.view': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
  ],
  'home_page.edit': ['admin', 'marketing'],

  // ── Login Screen ──────────────────────────────────────────────────────
  'login_screen.view': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
  ],
  'login_screen.edit': ['admin', 'marketing'],

  // ── New User Dashboard ────────────────────────────────────────────────
  'new_user_dashboard.view': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
  ],
  // Matrix puts Edit on super_admin + marketing only.
  'new_user_dashboard.edit': ['marketing'],

  // ── Student Verification ──────────────────────────────────────────────
  'student_verification.view': [
    'admin',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
  ],
  'student_verification.edit': [
    'admin',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
  ],

  // ── Admin Users (this app's own user list) ────────────────────────────
  'admin_users.view': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
  ],
  // Matrix: only super_admin can edit admin users (create/disable/change
  // role/reset MFA & password). Other admin-user actions all funnel
  // through this single permission.
  'admin_users.edit': [],

  // ── Feature Flags ─────────────────────────────────────────────────────
  // Invariant: every role with edit also gets view. The matrix omits
  // developer from view, but developer needs to see flags to edit them.
  'flags.view': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
  ],
  'flags.edit': ['admin', 'developer', 'tech_admin'],

  // ── Marketing ─────────────────────────────────────────────────────────
  'marketing.view': [
    'admin',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
  ],
  'marketing.edit': ['admin', 'marketing'],

  // ── Clinical Skills ───────────────────────────────────────────────────
  'clinical_skills.view': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
    'researcher',
  ],
  'clinical_skills.edit': ['admin', 'marketing', 'researcher'],

  // ── Practice Resources ────────────────────────────────────────────────
  'practice_resources.view': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
    'researcher',
  ],
  'practice_resources.edit': ['admin', 'marketing', 'researcher'],

  // ── Developer Tools ───────────────────────────────────────────────────
  'devtools.transaction_debug': ['admin', 'developer'],
  'devtools.invoices': ['admin', 'developer'],
  'devtools.eras': ['admin', 'developer'],
  'devtools.dejavoo_diagnostics': ['admin', 'developer'],
  'devtools.dejavoo_autofix': ['admin', 'developer'],

  // ── Stripe add-on revenue (analytics) ─────────────────────────────────
  // Not in the matrix; gates the Stripe add-on-revenue admin widget.
  // Stripe analytics and its supporting revenue data are admin-only.
  'stripe.addons.manage': ['admin'],

  // ── Device Blocklist (trial-abuse prevention) ─────────────────────────
  // Cross-clinic anti-fraud tooling: view recorded device fingerprints and
  // manage the global signup blocklist. Restricted to the technical roles
  // (plus super_admin, which auto-grants everything) since blocking a device
  // rejects future signups from it.
  'device_blocklist.view': ['admin', 'developer'],
  'device_blocklist.edit': ['admin', 'developer'],

  // ── Platform connector credentials (connection-api) ──────────────────
  // Stage/validate/promote the PLATFORM API key for connector vendors
  // (docs/connection-api/INFRASTRUCTURE_PLAN.md §3b). One key serves every
  // clinic on the vendor — a botched rotation is a platform-wide outage, so
  // this stays with the technical roles (incl. developer, who build and
  // dogfood the ops console — same posture as device_blocklist.*).
  'platform.manage_connector_credentials': ['admin', 'tech_admin', 'developer'],

  // ── Referrals (subscriber referral program) ───────────────────────────
  // Not in the matrix CSV yet. Broad view (same audience as analytics) since
  // it's read-only reporting; account_admin owns sending the rewards.
  'referrals.view': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
    'finance',
  ],

  // ── Internal Docs ─────────────────────────────────────────────────────
  // Broad: the internal docs are for the whole staff (dev/support/CS/etc.), so
  // every non-super role can read them. Narrow this list to restrict access.
  'docs.view': [
    'admin',
    'developer',
    'tech_admin',
    'account_admin',
    'account_support',
    'standard_user',
    'marketing',
    'finance',
    'researcher',
  ],
} as const satisfies Record<string, readonly NonSuperRole[]>;

export type Permission = keyof typeof PERMISSION_MATRIX;

// fallow-ignore-next-line unused-export
export const ALL_PERMISSIONS = Object.keys(PERMISSION_MATRIX) as readonly Permission[];

/**
 * Role → permission set, derived from PERMISSION_MATRIX.
 *
 * super_admin always gets every permission; everyone else only gets the
 * permissions whose row lists them. Roles that aren't on any row simply
 * end up with the empty set.
 */
// fallow-ignore-next-line unused-export
export const ROLE_PERMISSIONS: Record<AdminUserRoleKey, readonly Permission[]> = (() => {
  const result = {} as Record<AdminUserRoleKey, Permission[]>;
  for (const role of ADMIN_USER_ROLE_KEYS) result[role] = [];

  for (const [permission, roles] of Object.entries(PERMISSION_MATRIX) as [
    Permission,
    readonly NonSuperRole[],
  ][]) {
    result.super_admin.push(permission);
    for (const role of roles) result[role].push(permission);
  }

  return result;
})();

export const expandPermissions = (key: AdminUserRoleKey): Set<Permission> =>
  new Set(ROLE_PERMISSIONS[key]);

export const isAdminUserRoleKey = (s: unknown): s is AdminUserRoleKey =>
  typeof s === 'string' && (ADMIN_USER_ROLE_KEYS as readonly string[]).includes(s);
